import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../../utils/formatters';
import { Loader2, Edit3, Trash2, List } from 'lucide-react';

const CategorizationCenter = () => {

  const [transactions, setTransactions] = useState([]);

  const [merchantMemory, setMerchantMemory] = useState([]);

  const [selectedIds, setSelectedIds] = useState(new Set());

  const [suggestions, setSuggestions] = useState({});

  const [bulkCategory, setBulkCategory] = useState('');

  const [rememberMerchant, setRememberMerchant] = useState(true);

  const [loading, setLoading] = useState(true);

  const [suggesting, setSuggesting] = useState(false);

  const [applying, setApplying] = useState(false);

  const [error, setError] = useState(null);



  const fetchData = useCallback(async () => {

    setLoading(true);

    setError(null);

    try {

      const [txnRes, memoryRes] = await Promise.all([

        axios.get('/api/transactions', { params: { category: 'Uncategorized' } }),

        axios.get('/api/merchant-categories'),

      ]);

      setTransactions(txnRes.data);

      setMerchantMemory(memoryRes.data);

    } catch (err) {

      console.error(err);

      setError('Failed to load categorization data.');

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    fetchData();

  }, [fetchData]);



  const toggleSelection = (id) => {

    setSelectedIds((prev) => {

      const next = new Set(prev);

      if (next.has(id)) next.delete(id);

      else next.add(id);

      return next;

    });

  };



  const requestSuggestions = async () => {

    if (selectedIds.size === 0) return;

    setSuggesting(true);

    setError(null);

    try {

      const payload = {

        transactions: transactions

          .filter((txn) => selectedIds.has(txn.id))

          .map((txn) => ({ id: txn.id, description: txn.description, amount: txn.amount })),

      };

      const res = await axios.post('/api/categorize/ai', payload);

      const map = {};

      res.data.suggestions.forEach((item) => {

        map[item.id] = item;

      });

      setSuggestions(map);

    } catch (err) {

      console.error(err);

      setError(err.response?.data?.error || 'Failed to fetch AI suggestions.');

    } finally {

      setSuggesting(false);

    }

  };



  const applyTransactions = async (ids, category, options = {}) => {

    const { clearSelection = true, refreshAfter = true, manageApplying = true } = options;

    if (!Array.isArray(ids) || ids.length === 0 || !category) {

      setError('Select at least one transaction and choose a category.');

      return false;

    }

    if (manageApplying) {

      setApplying(true);

    }

    setError(null);

    try {

      await axios.post('/api/transactions/reassign', {

        transactionIds: ids,

        category,

        rememberMerchant,

      });

      if (clearSelection) {

        setSelectedIds((prev) => {

          const next = new Set(prev);

          ids.forEach((id) => next.delete(id));

          return next;

        });

      }

      setSuggestions((prev) => {

        const next = { ...prev };

        ids.forEach((id) => {

          delete next[id];

        });

        return next;

      });

      if (refreshAfter) {

        fetchData();

      }

      return true;

    } catch (err) {

      console.error(err);

      setError(err.response?.data?.error || 'Failed to update transactions.');

      return false;

    } finally {

      if (manageApplying) {

        setApplying(false);

      }

    }

  };



  const applyCategory = () => {

    applyTransactions(Array.from(selectedIds), bulkCategory).then((success) => {

      if (success) {

        setBulkCategory('');

      }

    });

  };



  const applySingleSuggestion = (txnId, category) => {

    applyTransactions([txnId], category);

  };



  const applyAllSuggestions = async () => {

    const suggestionEntries = Object.entries(suggestions);

    if (suggestionEntries.length === 0) {

      setError('No suggestions available to apply.');

      return;

    }

    setApplying(true);

    setError(null);



    const grouped = suggestionEntries.reduce((acc, [id, suggestion]) => {

      const key = suggestion.suggestedCategory;

      if (!acc[key]) acc[key] = [];

      acc[key].push(id);

      return acc;

    }, {});



    let success = true;

    for (const [category, ids] of Object.entries(grouped)) {

      const chunkSize = 50;

      for (let i = 0; i < ids.length; i += chunkSize) {

        const chunk = ids.slice(i, i + chunkSize);

        // eslint-disable-next-line no-await-in-loop

        const chunkSuccess = await applyTransactions(chunk, category, {

          clearSelection: false,

          refreshAfter: false,

          manageApplying: false,

        });

        if (!chunkSuccess) {

          success = false;

          break;

        }

      }

      if (!success) break;

    }



    if (success) {

      setSelectedIds(new Set());

      setSuggestions({});

      setBulkCategory('');

      fetchData();

    }

    setApplying(false);

  };



  const handleMemoryUpdate = async (entryId, category) => {

    try {

      await axios.put(`/api/merchant-categories/${entryId}`, { category });

      fetchData();

    } catch (err) {

      console.error(err);

      setError(err.response?.data?.error || 'Failed to update merchant rule.');

    }

  };



  const handleMemoryDelete = async (entryId) => {

    try {

      await axios.delete(`/api/merchant-categories/${entryId}`);

      fetchData();

    } catch (err) {

      console.error(err);

      setError(err.response?.data?.error || 'Failed to delete merchant rule.');

    }

  };



  return (

    <div className="container mx-auto p-8 space-y-8">

      <section className="bg-white p-6 rounded-lg shadow-xl border border-gray-200">

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">

          <div>

            <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">

              <Sparkles className="text-indigo-500" /> AI Categorization Assistant

            </h2>

            <p className="text-sm text-gray-500">

              Select uncategorized transactions, ask Gemini for suggestions, and remember merchant rules in one place.

            </p>

          </div>

          <div className="flex flex-wrap gap-3">

            <button

              onClick={requestSuggestions}

              disabled={selectedIds.size === 0 || suggesting}

              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-semibold disabled:bg-gray-400"

            >

              {suggesting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles size={16} className="mr-2" />}

              Get Suggestions

            </button>

            <div className="flex items-center space-x-2">

              <input

                type="text"

                value={bulkCategory}

                onChange={(e) => setBulkCategory(e.target.value)}

                placeholder="Category"

                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

              />

              <label className="inline-flex items-center space-x-2 text-sm text-gray-600">

                <input

                  type="checkbox"

                  checked={rememberMerchant}

                  onChange={(e) => setRememberMerchant(e.target.checked)}

                />

                <span>Remember merchants</span>

              </label>

              <button

                onClick={applyCategory}

                disabled={selectedIds.size === 0 || !bulkCategory || applying}

                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 disabled:bg-gray-400"

              >

                {applying ? 'Applying...' : 'Apply Category'}

              </button>

              <button

                onClick={applyAllSuggestions}

                disabled={Object.keys(suggestions).length === 0 || applying}

                className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700 disabled:bg-gray-400"

              >

                {applying ? 'Working...' : 'Apply All Suggestions'}

              </button>

            </div>

          </div>

        </div>



        {loading ? (

          <div className="flex justify-center py-6">

            <Loader2 className="animate-spin text-indigo-600" size={32} />

          </div>

        ) : transactions.length === 0 ? (

          <p className="text-sm text-gray-500">No uncategorized transactions found. Great job!</p>

        ) : (

          <div className="overflow-x-auto">

            <table className="min-w-full divide-y divide-gray-200 text-sm">

              <thead className="bg-gray-50">

                <tr>

                  <th className="px-4 py-2">

                    <input

                      type="checkbox"

                      checked={selectedIds.size > 0 && selectedIds.size === transactions.length}

                      onChange={(e) =>

                        setSelectedIds(e.target.checked ? new Set(transactions.map((txn) => txn.id)) : new Set())

                      }

                    />

                  </th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Description</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Amount</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Suggested</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Reason</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>

                </tr>

              </thead>

              <tbody className="divide-y divide-gray-100">

                {transactions.map((txn) => {

                  const suggestion = suggestions[txn.id];

                  return (

                    <tr key={txn.id}>

                      <td className="px-4 py-2">

                        <input

                          type="checkbox"

                          checked={selectedIds.has(txn.id)}

                          onChange={() => toggleSelection(txn.id)}

                        />

                      </td>

                      <td className="px-4 py-2 font-medium text-gray-800">{txn.description}</td>

                      <td className="px-4 py-2 text-gray-700">{formatCurrency(txn.amount, 2)}</td>

                      <td className="px-4 py-2 text-gray-700">

                        {suggestion ? (

                          <button

                            type="button"

                            onClick={() => setBulkCategory(suggestion.suggestedCategory)}

                            className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100"

                            title="Use this category for bulk apply"

                          >

                            {suggestion.suggestedCategory}

                          </button>

                        ) : (

                          <span className="text-xs text-gray-400">None</span>

                        )}

                      </td>

                      <td className="px-4 py-2 text-gray-500 text-xs min-w-[180px]">{suggestion?.reason || ''}</td>

                      <td className="px-4 py-2 text-gray-700">

                        {suggestion ? (

                          <button

                            onClick={() => applySingleSuggestion(txn.id, suggestion.suggestedCategory)}

                            className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md bg-green-50 text-green-700 hover:bg-green-100"

                          >

                            Apply

                          </button>

                        ) : (

                          <span className="text-xs text-gray-400">-</span>

                        )}

                      </td>

                    </tr>

                  );

                })}

              </tbody>

            </table>

          </div>

        )}

      </section>



      <section className="bg-white p-6 rounded-lg shadow-xl border border-gray-200">

        <div className="flex items-center justify-between mb-4">

          <div>

            <h3 className="text-xl font-semibold text-gray-800">Saved Merchant Rules</h3>

            <p className="text-sm text-gray-500">These rules are applied automatically during future imports.</p>

          </div>

        </div>

        {merchantMemory.length === 0 ? (

          <p className="text-sm text-gray-500">No saved rules yet.</p>

        ) : (

          <div className="overflow-x-auto">

            <table className="min-w-full divide-y divide-gray-200 text-sm">

              <thead className="bg-gray-50">

                <tr>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Merchant</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Category</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Source</th>

                  <th className="px-4 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">Actions</th>

                </tr>

              </thead>

              <tbody className="divide-y divide-gray-100">

                {merchantMemory.map((entry) => (

                  <tr key={entry.id}>

                    <td className="px-4 py-2 font-medium text-gray-800">{entry.displayName || entry.merchantKey}</td>

                    <td className="px-4 py-2">

                      <input

                        type="text"

                        value={entry.category}

                        onChange={(e) =>

                          setMerchantMemory((prev) =>

                            prev.map((item) =>

                              item.id === entry.id ? { ...item, category: e.target.value } : item

                            )

                          )

                        }

                        onBlur={(e) => handleMemoryUpdate(entry.id, e.target.value)}

                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                      />

                    </td>

                    <td className="px-4 py-2 text-gray-600 text-xs capitalize">{entry.source || 'manual'}</td>

                    <td className="px-4 py-2 text-right">

                      <button

                        onClick={() => handleMemoryDelete(entry.id)}

                        className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md bg-red-50 text-red-600 hover:bg-red-100"

                      >

                        <Trash2 size={14} className="mr-1" />

                        Delete

                      </button>

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        )}

      </section>



      {error && (

        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>

      )}

    </div>

  );

};




























export default CategorizationCenter;
